import React, { FC, useMemo, useState } from "react";
import { Modal, Select, type StatusOption } from "@pd/pd-ui-kit";
import { AxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetDealersForSelect } from "~/entity/Dealers";
import { EmployeeService } from "~/entity/Employee/api/service";
import { useGetPositionsOptions } from "~/pages/StaffEmployeePersonal/hook/useGetPositionsOptions";
import { useNotify } from "~/shared/hooks/useNotify";

import s from "./RestoreEmployeeModal.less";

interface IRestoreEmployeeForm {
  position: StatusOption | null;
  dealer: StatusOption | null;
}

interface IRestoreEmployeeModalProps {
  employeeId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const RestoreEmployeeModal: FC<IRestoreEmployeeModalProps> = ({
  employeeId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotify();
  const [saving, setSaving] = useState(false);

  const { control, watch } = useForm<IRestoreEmployeeForm>({
    defaultValues: {
      position: null,
      dealer: null,
    },
  });

  const positionValue = watch("position");
  const dealerValue = watch("dealer");

  const {
    options: positionOptions,
    loading: positionsLoading,
    onScrollBottom: onScrollBottomPositions,
    searchText: searchPosition,
  } = useGetPositionsOptions();

  const positionSelectOptions = useMemo<StatusOption[]>(
    () =>
      positionOptions
        .filter((option) => option.value != null)
        .map((option) => ({
          value: option.value as string | number,
          label: option.label || "",
        })),
    [positionOptions],
  );

  const {
    options: dealerOptions,
    loading: dealersLoading,
    onSearch: searchDealer,
    onScrollBottom: onScrollBottomDealers,
    onOpenChange: onOpenChangeDealers,
  } = useGetDealersForSelect(true, { pageSize: 100, immediate: true });

  const getRestoreErrorMessage = (error: unknown) => {
    const fallback = t("errors.VALIDATION_ERROR");
    if (error instanceof AxiosError) {
      const message = (
        error.response?.data as
          | { message?: string | { message?: string } }
          | undefined
      )?.message;
      if (typeof message === "string" && message) {
        return message;
      }
      if (message && typeof message === "object" && message.message) {
        return message.message;
      }
    }
    return fallback;
  };

  const handleSubmit = async () => {
    if (!positionValue?.value || !dealerValue?.value) {
      return;
    }
    setSaving(true);
    try {
      await EmployeeService.PUT.restore(employeeId, {
        positionId: Number(positionValue.value),
        dealerId: Number(dealerValue.value),
      });
      notifySuccess(t("employee.restoreSuccess"));
      onSuccess();
      onClose();
    } catch (error) {
      notifyError(getRestoreErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
      return true;
    }
    return false;
  };

  return (
    <Modal
      open
      title={t("employee.restoreTitle")}
      onCancel={onClose}
      centered
      size="small"
      maskClosable={false}
      cancelButton={{ children: t("ui.cancel"), disabled: saving }}
      okButton={{
        children: t("employee.restoreSubmit"),
        disabled: !positionValue?.value || !dealerValue?.value,
        loading: saving,
        onClick: handleSubmit,
      }}
    >
      <div className={s.form}>
        <Controller
          name="position"
          control={control}
          render={({ onChange, value }) => (
            <Select
              labelInValue
              showSearch
              value={value}
              loading={positionsLoading}
              options={positionSelectOptions}
              placeholder={t("employee.restoreSelectPosition")}
              onChange={(option) => onChange(option || null)}
              onSearch={searchPosition}
              onPopupScroll={(event) => {
                if (handlePopupScroll(event)) {
                  onScrollBottomPositions();
                }
              }}
              filterOption={false}
            />
          )}
        />

        <Controller
          name="dealer"
          control={control}
          render={({ onChange, value }) => (
            <Select
              labelInValue
              showSearch
              value={value}
              loading={dealersLoading}
              options={dealerOptions}
              placeholder={t("employee.restoreSelectDealer")}
              onChange={(option) => onChange(option || null)}
              onSearch={searchDealer}
              onOpenChange={onOpenChangeDealers}
              onPopupScroll={(event) => {
                if (handlePopupScroll(event)) {
                  onScrollBottomDealers();
                }
              }}
              filterOption={false}
            />
          )}
        />
      </div>
    </Modal>
  );
};

export default RestoreEmployeeModal;
